import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { canConfirm, getUserType } from "../permissions";
import API_BASE from "../config";

const UnconfirmPayments = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const userType = getUserType();
    const allowConfirm = canConfirm(userType, "unconfirm_payments");
    const [data, setData] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    
    // Initialize state from URL params
    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [modeFilter, setModeFilter] = useState(searchParams.get("modeFilter") || "");
    const [paymentForFilter, setPaymentForFilter] = useState(searchParams.get("paymentForFilter") || "");
    const [projectFilter, setProjectFilter] = useState(searchParams.get("projectFilter") || "");
    const [amountSort, setAmountSort] = useState(searchParams.get("amountSort") || "");
    const [dateSort, setDateSort] = useState(searchParams.get("dateSort") || "");
    const [page, setPage] = useState(parseInt(searchParams.get("page"), 10) || 1);
    const perPage = 10;

    // Update URL params when state changes
    useEffect(() => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (modeFilter) params.set("modeFilter", modeFilter);
        if (paymentForFilter) params.set("paymentForFilter", paymentForFilter);
        if (projectFilter) params.set("projectFilter", projectFilter);
        if (amountSort) params.set("amountSort", amountSort);
        if (dateSort) params.set("dateSort", dateSort);
        if (page > 1) params.set("page", page);
        setSearchParams(params, { replace: true });
    }, [search, modeFilter, paymentForFilter, projectFilter, amountSort, dateSort, page, setSearchParams]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetch(`${API_BASE}/api/allrows`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ token: "3232", table: "payments" }),
            }).then((r) => r.json()),
            fetch(`${API_BASE}/api/allrows`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ token: "3232", table: "projects" })
            }).then(r => r.json())
        ])
            .then(([paymentsJson, projectsJson]) => {
                setLoading(false);
                if (Array.isArray(paymentsJson.data)) {
                    // Only keep payments where status === "Received"
                    const received = paymentsJson.data.filter((p) => p.status === "Received");
                    setData(received);
                } else {
                    setError("Failed to load payments.");
                }

                if (Array.isArray(projectsJson.data)) setProjects(projectsJson.data);
            })
            .catch(() => {
                setLoading(false);
                setError("Error fetching data.");
            });
    }, []);

    // Create a mapping for fast project lookups
    const projectIdMap = {};
    projects.forEach(p => {
        if (p.id) projectIdMap[p.id] = p.name || p.shortform || `Project ${p.id}`;
    });

    const parseDate = (dateStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.includes("/") ? dateStr.split("/") : dateStr.split("-");
        if (parts.length === 3) {
            let year = parseInt(parts[2], 10);
            if (year < 100) year += 2000;
            return new Date(year, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10)).getTime();
        }
        return new Date(dateStr).getTime() || 0;
    };

    let filtered = data.filter((i) =>
        Object.values(i).join(" ").toLowerCase().includes(search.toLowerCase())
    );
    if (modeFilter) filtered = filtered.filter((i) => i.payment_mode === modeFilter);
    if (paymentForFilter) filtered = filtered.filter((i) => i.payment_for === paymentForFilter);
    if (projectFilter) filtered = filtered.filter((i) => i.project_id === projectFilter);

    if (amountSort === "asc") filtered.sort((a, b) => Number(a.amount) - Number(b.amount));
    if (amountSort === "desc") filtered.sort((a, b) => Number(b.amount) - Number(a.amount));
    if (dateSort === "asc") filtered.sort((a, b) => parseDate(a.payment_collect) - parseDate(b.payment_collect));
    if (dateSort === "desc") filtered.sort((a, b) => parseDate(b.payment_collect) - parseDate(a.payment_collect));

    const totalPages = Math.ceil(filtered.length / perPage);
    const rows = filtered.slice((page - 1) * perPage, page * perPage);

    const fmt = (v) => {
        const n = Number(v);
        return isNaN(n) || n === 0 ? "—" : "₹" + n.toLocaleString("en-IN");
    };

    const paymentModes = [...new Set(data.map((d) => d.payment_mode).filter(Boolean))];
    const paymentFors = [...new Set(data.map((d) => d.payment_for).filter(Boolean))];

    return (
        <>
            <style>{`
        .up-page { display:flex; flex-direction:column; gap:20px; }
        .up-header { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .up-title-wrap { display:flex; align-items:center; gap:12px; }
        .up-title { font-size:22px; font-weight:700; color:#0f172a; }
        .up-count { font-size:13px; color:#64748b; margin-top:2px; }
        .up-badge-header { background:linear-gradient(135deg,#fbbf24,#f59e0b); color:#78350f; padding:5px 14px; border-radius:20px; font-size:12px; font-weight:700; letter-spacing:.3px; }
        .up-filters { background:#fff; padding:16px; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); display:flex; gap:12px; flex-wrap:wrap; align-items:center; }
        .up-filters input,.up-filters select { padding:10px 14px; border-radius:10px; border:1px solid #e2e8f0; outline:none; min-width:150px; font-size:13px; font-family:'Inter',sans-serif; color:#334155; background:#f8fafc; transition:all .2s; }
        .up-filters input { flex:1; min-width:200px; }
        .up-filters input:focus,.up-filters select:focus { border-color:#fbbf24; box-shadow:0 0 0 3px rgba(251,191,36,.12); background:#fff; }
        .up-wrap { background:#fff; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); overflow-x:auto; }
        .up-table { width:100%; border-collapse:collapse; min-width:1000px; }
        .up-table th,.up-table td { padding:13px 14px; font-size:13px; text-align:left; border-bottom:1px solid #f1f5f9; }
        .up-table th { background:#fffbeb; color:#92400e; font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:.5px; white-space:nowrap; position:sticky; top:0; }
        .up-table tbody tr { transition:background .15s; }
        .up-table tbody tr:hover { background:#fffbeb; }
        .up-table td { color:#334155; font-weight:500; }
        .up-cust { display:flex; flex-direction:column; gap:1px; }
        .up-cust-name { font-weight:600; color:#0f172a; text-transform:capitalize; }
        .up-cust-id { font-size:11px; color:#94a3b8; }
        .up-amt { font-weight:700; color:#0f172a; font-variant-numeric:tabular-nums; }
        .up-badge { display:inline-block; padding:4px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; background:#fffbeb; color:#d97706; border:1px solid #fcd34d; }
        .up-unit { font-weight:600; color:#2563eb; }
        .up-date { color:#64748b; font-size:12px; white-space:nowrap; }
        .up-confirm-btn { padding:8px 18px; border:none; border-radius:8px; background:linear-gradient(135deg,#f59e0b,#d97706); color:#fff; cursor:pointer; font-size:12px; font-weight:700; font-family:'Inter',sans-serif; transition:all .25s; white-space:nowrap; display:flex; align-items:center; gap:6px; box-shadow:0 2px 6px rgba(217,119,6,.18); }
        .up-confirm-btn:hover { background:linear-gradient(135deg,#d97706,#b45309); box-shadow:0 4px 14px rgba(217,119,6,.3); transform:translateY(-1px); }
        .up-pag { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .up-pag-info { font-size:13px; color:#64748b; }
        .up-pag-btns { display:flex; align-items:center; gap:6px; }
        .up-pag-btns button { padding:8px 14px; border-radius:8px; border:1px solid #e2e8f0; background:#fff; font-size:13px; font-weight:500; color:#334155; cursor:pointer; transition:all .2s; font-family:'Inter',sans-serif; }
        .up-pag-btns button:hover:not(:disabled) { background:#f1f5f9; }
        .up-pag-btns button:disabled { opacity:.4; cursor:not-allowed; }
        .up-pag-btns span { font-size:13px; color:#64748b; padding:0 6px; }
        .up-load { display:flex; flex-direction:column; align-items:center; padding:60px 20px; gap:14px; }
        .up-spin { width:36px; height:36px; border:3px solid #fde68a; border-top-color:#f59e0b; border-radius:50%; animation:up-sp .7s linear infinite; }
        @keyframes up-sp { to{transform:rotate(360deg)} }
        .up-load-t { font-size:14px; color:#64748b; }
        .up-err { padding:16px; background:#fef2f2; border:1px solid #fecaca; border-radius:12px; color:#dc2626; font-size:14px; text-align:center; }
        .up-empty { padding:40px 20px; text-align:center; color:#94a3b8; font-size:14px; }
        .up-empty-icon { font-size:40px; margin-bottom:10px; }
        .up-clear-btn { padding:10px 16px; border:1px solid #e2e8f0; border-radius:10px; background:#fff; color:#475569; font-size:13px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; height: 100%; white-space: nowrap; }
        .up-clear-btn:hover { background:#f1f5f9; color:#0f172a; border-color:#cbd5e1; }
        @media(max-width:640px){
          .up-filters{flex-direction:column;align-items:stretch;}
          .up-filters input,.up-filters select, .up-clear-btn{min-width:100%;}
          .up-pag{flex-direction:column;align-items:stretch;text-align:center;}
          .up-pag-btns{justify-content:center;}
        }
      `}</style>

            <div className="up-page">
                <div className="up-header">
                    <div>
                        <div className="up-title-wrap">
                            <h2 className="up-title">📋 Unconfirmed Payments</h2>
                            <span className="up-badge-header">RECEIVED</span>
                        </div>
                        <div className="up-count">
                            {loading ? "Loading..." : `${filtered.length} payment${filtered.length !== 1 ? "s" : ""} awaiting confirmation`}
                        </div>
                    </div>
                </div>

                <div className="up-filters">
                    <input
                        placeholder="🔍 Search by name, unit, ID..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                    <select value={projectFilter} onChange={(e) => { setProjectFilter(e.target.value); setPage(1); }}>
                        <option value="">All Projects</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.shortform}</option>)}
                    </select>
                    <select value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}>
                        <option value="">Payment Mode</option>
                        {paymentModes.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select value={paymentForFilter} onChange={(e) => { setPaymentForFilter(e.target.value); setPage(1); }}>
                        <option value="">All Types (For)</option>
                        {paymentFors.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <select value={amountSort} onChange={(e) => setAmountSort(e.target.value)}>
                        <option value="">Sort by Amount</option>
                        <option value="asc">Low → High</option>
                        <option value="desc">High → Low</option>
                    </select>
                    <select value={dateSort} onChange={(e) => setDateSort(e.target.value)}>
                        <option value="">Sort by Date</option>
                        <option value="asc">Oldest First</option>
                        <option value="desc">Newest First</option>
                    </select>
                    {(search || modeFilter || projectFilter || paymentForFilter || amountSort || dateSort) && (
                        <button 
                            className="up-clear-btn" 
                            onClick={() => {
                                setSearch("");
                                setModeFilter("");
                                setProjectFilter("");
                                setPaymentForFilter("");
                                setAmountSort("");
                                setDateSort("");
                                setPage(1);
                                setSearchParams(new URLSearchParams(), { replace: true });
                            }}
                            title="Clear all filters"
                        >
                            ✖ Clear
                        </button>
                    )}
                </div>

                {error && <div className="up-err">⚠️ {error}</div>}

                {loading ? (
                    <div className="up-load">
                        <div className="up-spin" />
                        <div className="up-load-t">Loading received payments...</div>
                    </div>
                ) : (
                    <>
                        <div className="up-wrap">
                            <table className="up-table">
                                <thead>
                                    <tr>
                                        <th>Customer</th>
                                        <th>Payment ID</th>
                                        <th>Project</th>
                                        <th>Unit</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Mode</th>
                                        <th>For</th>
                                        <th>Collected</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="up-empty">
                                                    <div className="up-empty-icon">✅</div>
                                                    No unconfirmed payments found
                                                </div>
                                            </td>
                                        </tr>
                                    ) : rows.map((p) => (
                                        <tr key={p.id}>
                                            <td>
                                                <div className="up-cust">
                                                    <span className="up-cust-name">{p.customer_name}</span>
                                                    <span className="up-cust-id">Booking: {p.booking_id}</span>
                                                </div>
                                            </td>
                                            <td style={{ fontWeight: 600, color: "#475569" }}>{p.payment_id}</td>
                                            <td><span className="up-unit">{projectIdMap[p.project_id] || "—"}</span></td>
                                            <td><span className="up-unit">{p.unit}</span></td>
                                            <td><span className="up-amt">{fmt(p.amount)}</span></td>
                                            <td><span className="up-badge">{p.status}</span></td>
                                            <td>{p.payment_mode || "—"}</td>
                                            <td>{p.payment_for || "—"}</td>
                                            <td><span className="up-date">{p.payment_collect || "—"}</span></td>
                                            <td>
                                                {allowConfirm ? (
                                                    <button
                                                        className="up-confirm-btn"
                                                        onClick={() => navigate("/dashboard/payments/confirm", { state: { payment: p } })}
                                                    >
                                                        ✔ Confirm
                                                    </button>
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>View Only</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="up-pag">
                                <div className="up-pag-info">
                                    Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
                                </div>
                                <div className="up-pag-btns">
                                    <button disabled={page === 1} onClick={() => setPage(1)}>First</button>
                                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default UnconfirmPayments;
